import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { PlanRun } from "../types";

// Define the shape of messages exchanged over WebSocket (mirroring backend)
type WSMessage =
  | { type: "INIT"; run: PlanRun; presence: any[] }
  | { type: "UPDATE_RUN"; run: PlanRun }
  | { type: "UPDATE_PRESENCE"; presence: any[] }
  | { type: "UPDATE_CHECKLIST"; checklist: number[] }
  | { type: "PING" }
  | { type: "PONG" };

type IncomingMessage =
  | {
      type: "JOIN";
      user: { id: string; name: string; initials: string; imageUrl?: string };
    }
  | { type: "LEAVE"; userId: string }
  | { type: "TOGGLE_PREREQUISITE"; index: number; checked: boolean }
  | { type: "HEARTBEAT" };

interface UseRunWebSocketProps {
  runId: string | undefined;
  user: any; // Clerk user object
  onRunUpdate: (run: PlanRun) => void;
  onPresenceUpdate: (presence: any[]) => void;
  onChecklistUpdate: (checklist: number[]) => void;
}

export const useRunWebSocket = ({
  runId,
  user,
  onRunUpdate,
  onPresenceUpdate,
  onChecklistUpdate,
}: UseRunWebSocketProps) => {
  const { getToken } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const heartbeatIntervalRef = useRef<number | null>(null);

  // Keep latest callbacks in ref to avoid reconnecting when they change
  const callbacksRef = useRef({
    onRunUpdate,
    onPresenceUpdate,
    onChecklistUpdate,
  });
  useEffect(() => {
    callbacksRef.current = { onRunUpdate, onPresenceUpdate, onChecklistUpdate };
  }, [onRunUpdate, onPresenceUpdate, onChecklistUpdate]);

  const togglePrerequisite = useCallback((index: number, checked: boolean) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: "TOGGLE_PREREQUISITE", index, checked }),
      );
    }
  }, []);

  const connect = useCallback(async () => {
    if (!runId || !user) return;

    // Prevent multiple connections
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    )
      return;

    try {
      let apiBase = import.meta.env.VITE_API_URL || "http://localhost:8787/api";

      if (!apiBase.endsWith("/api")) {
        apiBase = `${apiBase}/api`;
      }

      const wsProtocol = apiBase.startsWith("https") ? "wss" : "ws";
      const hostPath = apiBase.replace(/^https?:\/\//, "");

      const token = await getToken();
      const wsUrl = `${wsProtocol}://${hostPath}/runs/${runId}/ws?__clerk_token=${token}`;

      console.log("Connecting to Run WebSocket...");
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        console.log("Run WebSocket connected");

        // Send JOIN message
        const joinMsg: IncomingMessage = {
          type: "JOIN",
          user: {
            id: user.id,
            name: user.fullName || "User",
            initials:
              (
                (user.firstName?.[0] || "") + (user.lastName?.[0] || "")
              ).toUpperCase() || "??",
            imageUrl: user.imageUrl,
          },
        };
        ws.send(JSON.stringify(joinMsg));

        // Start Heartbeat
        if (heartbeatIntervalRef.current)
          clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "HEARTBEAT" }));
          }
        }, 300000); // 5 minutes
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WSMessage;
          const { onRunUpdate, onPresenceUpdate, onChecklistUpdate } =
            callbacksRef.current;

          switch (msg.type) {
            case "INIT":
            case "UPDATE_RUN":
              onRunUpdate(msg.run);
              if ("presence" in msg) onPresenceUpdate(msg.presence);
              break;
            case "UPDATE_PRESENCE":
              onPresenceUpdate(msg.presence);
              break;
            case "UPDATE_CHECKLIST":
              onChecklistUpdate(msg.checklist);
              break;
            case "PONG":
              break;
          }
        } catch (e) {
          console.error("Error parsing WS message", e);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        console.log(
          `Run WebSocket disconnected: ${event.code} ${event.reason}`,
        );
        if (heartbeatIntervalRef.current)
          clearInterval(heartbeatIntervalRef.current);
        wsRef.current = null;

        // Retry connection after 3s unless it was a clean close
        if (event.code !== 1000) {
          setTimeout(connect, 3000);
        }
      };

      ws.onerror = (e) => {
        console.error("Run WebSocket error", e);
        ws.close();
      };
    } catch (e) {
      console.error("Failed to connect Run WebSocket", e);
    }
  }, [runId, user?.id, getToken]); // Only reconnect if runId or user.id changes

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({ type: "LEAVE", userId: user?.id }),
          );
        }
        wsRef.current.close(1000, "Component unmounted");
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [connect, user?.id]);

  return { isConnected, togglePrerequisite };
};
